import { lookupsJson } from '@ourbranch/lookups';
import { isWindHailDeductibleOverridden, resolveWindHailDeductibleAmount } from '@ourbranch/farm-utils';
import { appConfig } from 'brand-config';

const percentageOptions = lookupsJson.farm.windstormOrHail;
const DEFAULT_FLOOR = 1500;

/**
 * Calculate wind/hail deductible options for a Coverage F item.
 * Reuses the same logic as the FO6 Structures per-risk wind/hail deductible
 * (staff-fe/src/common/components/brand-components/forreston/farm/base/IL/deductible-wind-hail.jsx).
 *
 * @param {Object} params
 * @param {number} params.coverage - The item's coverage limit
 * @param {number} params.allPerilDeductible - The item's all-peril deductible
 * @param {number} [params.floor=1500] - Minimum calculated wind/hail deductible amount.
 *   CFM floors at $2,500 (issue #32483); other carriers use the $1,500 default.
 * @param {number|null} [params.approvedValue] - An approved wind/hail deductible exception currently
 *   held by the risk/item (see `approvedWindHailDeductible`). It is kept as an option even where the
 *   rule would not offer it. #36996
 * @returns {{ options: Array<{id: number, value: string}>, isOverride: boolean, targetValue: number|null }}
 */
export function getWindHailOptions({ coverage, allPerilDeductible, floor = DEFAULT_FLOOR, approvedValue = null }) {
  const result = getRuleWindHailOptions({ coverage, allPerilDeductible, floor });
  if (approvedValue == null || result.options.some((o) => o.id === approvedValue)) {
    return result;
  }
  // #36996: GO's 1% on an imported CFM structure / Coverage F article. Labelled with the amount GO
  // applied (and the dec prints): never below the all-peril deductible or $2,500. Selectable, so an
  // authority user can move it to one of the rule's own options.
  const amount = resolveWindHailDeductibleAmount({
    deductibleWindHail: approvedValue,
    windHailDeductibleOverride: approvedValue,
    coverageLimit: coverage,
    deductibleAllOther: allPerilDeductible
  });
  const label = approvedValue < 1 ? `${+(approvedValue * 100).toFixed(2)}%` : `$${approvedValue.toLocaleString()}`;
  return {
    ...result,
    options: [
      ...result.options,
      { id: approvedValue, value: `${label} ($${amount.toLocaleString()}) – approved exception` }
    ],
    isOverride: false
  };
}

/**
 * The approved wind/hail deductible a risk's coverages (or a Coverage F item) currently hold, or null.
 * Only while `deductibleWindHail` still equals `windHailDeductibleOverride`: once staff pick another
 * option the approval no longer applies. #36996
 */
export function approvedWindHailDeductible(holder) {
  return isWindHailDeductibleOverridden(holder) ? Number(holder.windHailDeductibleOverride) : null;
}

/**
 * What staff see under a wind/hail field holding an approved exception, or null when it holds none.
 * Mirrors the #35617 coverage-limit override: on a brand with the override (`featureFlags.
 * coverageLimitOverride`), staff without `canOverrideCoverageLimit` find the field locked and are
 * pointed to underwriting; staff with it may move the value to one of the rule's options. #36996
 *
 * @returns {{ locked: boolean, description: string } | null}
 */
export function windHailApprovalDescription({ holder, canOverrideCoverageLimit }) {
  if (approvedWindHailDeductible(holder) == null) {
    return null;
  }
  const locked = Boolean(appConfig?.featureFlags?.coverageLimitOverride) && !canOverrideCoverageLimit;
  const reason = holder.windHailDeductibleOverrideReason || 'Approved wind/hail deductible exception.';
  return {
    locked,
    description: locked
      ? `${reason} Please reach out to underwriting to make changes.`
      : `${reason} Approved by ${holder.windHailDeductibleOverrideBy || 'an authorized user'}.`
  };
}

function getRuleWindHailOptions({ coverage, allPerilDeductible, floor }) {
  const FLOOR = floor;
  const allPeril = Number(allPerilDeductible) || 0;

  if (!coverage) {
    const percentageIds = new Set(percentageOptions.map((o) => o.id));
    if (allPeril > 0 && !percentageIds.has(allPeril)) {
      return {
        options: [...percentageOptions, { id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` }],
        isOverride: false,
        targetValue: allPeril
      };
    }
    return { options: percentageOptions, isOverride: false, targetValue: null };
  }

  const dollarAmounts = percentageOptions.map(({ id }) => Math.round(coverage * id));
  const highest = dollarAmounts[dollarAmounts.length - 1];
  const lowest = dollarAmounts[0];

  // All Peril exceeds all percentage options — wind hail must match
  if (allPeril > highest && allPeril > FLOOR) {
    return {
      options: [{ id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` }],
      isOverride: true,
      targetValue: allPeril
    };
  }

  const opts = [];

  // Wind/Hail must be at least as high as FLOOR or All-Peril, whichever is higher
  const minimumAllowed = Math.max(FLOOR, allPeril);

  // Floor option when the lowest percentage doesn't reach the minimum
  if (lowest < minimumAllowed) {
    opts.push({ id: minimumAllowed, value: `$${minimumAllowed.toLocaleString()}` });
  }

  // Percentage options whose dollar amount meets the minimum (FLOOR or All-Peril)
  for (let i = 0; i < percentageOptions.length; i++) {
    if (dollarAmounts[i] >= minimumAllowed) {
      const { id, value } = percentageOptions[i];
      opts.push({ id, value: `${value} ($${dollarAmounts[i].toLocaleString()})` });
    }
  }

  // "Match All Peril" when it sits between percentages and isn't an exact match
  // Don't add if it's already been added as the minimum floor option
  if (allPeril > lowest && allPeril < highest && !dollarAmounts.includes(allPeril) && allPeril !== minimumAllowed) {
    opts.push({ id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` });
  }

  return { options: opts, isOverride: false, targetValue: opts[0]?.id ?? FLOOR };
}

/**
 * Resolve the wind/hail deductible a form should hold for an FO-6 structure, mirroring the
 * `deductible-wind-hail` brand component's auto-correct effect: keep the current value when it is a
 * valid option, otherwise snap to the computed target. Pre-applying this into the offer form's
 * initialValues keeps them in step with what the effect sets, so a structure whose stored/seeded
 * wind/hail deductible is below the brand floor (e.g. a $1,500 seed under CFM's $2,500 floor) doesn't
 * leave the offer permanently dirty — which otherwise hid "Send to underwriting" behind a stuck
 * "Update Offer Price". GitHub issue #35186.
 *
 * @param {Object} params
 * @param {number} params.coverage - The structure's Coverage E limit.
 * @param {number} params.deductibleAllOther - The structure's all-peril deductible.
 * @param {number} params.deductibleWindHail - The current wind/hail deductible.
 * @param {number} [params.floor] - Brand wind/hail floor (CFM $2,500; default $1,500).
 * @param {number|null} [params.approvedValue] - An approved exception the risk holds (#36996).
 * @returns {number} The wind/hail deductible the form should hold.
 */
export function settleWindHailDeductible({ coverage, deductibleAllOther, deductibleWindHail, floor, approvedValue }) {
  const { options, targetValue } = getWindHailOptions({
    coverage,
    allPerilDeductible: deductibleAllOther,
    floor,
    approvedValue
  });
  if (targetValue == null) {
    return deductibleWindHail;
  }
  const validIds = new Set(options.map((o) => o.id));
  if (validIds.has(Number(deductibleWindHail))) {
    return deductibleWindHail;
  }
  return targetValue;
}
